import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, apiPostForm, type PostValue } from './client'

/**
 * Settings -> Contracts (`settings/customersV2/contracts`), calea noua.
 *
 * Perechile vechi, din `settings/CustomersV2.php`, `settings/Customer_contracts.php`
 * si `settings/Customer_services.php` - lista intreaga e in capul lui
 * `App\Controller\SettingsContractsController`.
 *
 * Calea NU poarta `/api/v2`: il pune `apiGet` / `apiPost`.
 */

/** Raspunsul unei liste, in forma pe care o astepta DataTables. */
export type TablePage<T> = {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: T[]
}

export type ContractsTableRequest = {
  start: number
  length: number
  orderColumn: number
  orderName: string
  orderDir: 'asc' | 'desc'
  search: string
  /** Numai contractele unei firme (`Customer_contracts::index/{firma}`, fila din fisa clientului). */
  company?: number
}

export function contractsTableParams(r: ContractsTableRequest): URLSearchParams {
  const p = new URLSearchParams()
  p.set('draw', '1')
  if (r.company !== undefined && r.company > 0) p.set('company', String(r.company))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  p.set(`columns[${r.orderColumn}][name]`, r.orderName)
  p.set('search[value]', r.search)
  return p
}

/* -------------------------------------------------------------------------
 * Cadrul si bara de file
 * ---------------------------------------------------------------------- */

/** O fila din bara ecranului; `to` nevid = fila duce la ALT ecran. */
export type ContractTab = {
  id: string
  label: string
  href: string
  to: string
}

export type ContractsShell = {
  pageTitle: string
  moduleUrl: string
  tabs: ContractTab[]
  /** "Intervention" sau "On-site", dupa furnizor. */
  interventionLabel: string
  canCreate: boolean
  canEdit: boolean
  canReadDocuments: boolean
}

export const TABS = [
  'contractServiceSettings',
  'serviceList',
  'service_properties',
  'allContracts',
] as const

export type ContractTabId = (typeof TABS)[number]

export function isContractTab(value: string): value is ContractTabId {
  return (TABS as readonly string[]).includes(value)
}

export const contractsShellQuery = queryOptions({
  queryKey: ['settings', 'contracts', 'shell'],
  queryFn: () => apiGet<ContractsShell>('/settings/contracts'),
})

/* -------------------------------------------------------------------------
 * General Settings: cele opt liste de tipuri
 * ---------------------------------------------------------------------- */

export type ContractTypeRow = {
  ID: number
  name: string
  /** "1" / "0", ca in `contracts.js`. */
  status: string
  minutes?: number
  calc_days?: number
  calc_hours?: number
  calc_minutes?: number
  has_quantity?: number
  has_category?: number
  category_type?: string
}

/** `name` = doar numele; `minutes` = zile/ore/minute; `flags` = unitatile de masura. */
export type TypeShape = 'name' | 'minutes' | 'flags'

export type ContractTypeList = {
  kind: string
  label: string
  shape: TypeShape
  rows: ContractTypeRow[]
}

export type GeneralSettings = {
  lists: ContractTypeList[]
  /** Valorile din enum-ul `category_type`. */
  categoryTypes: string[]
  canEdit: boolean
  canCreate: boolean
}

export const generalSettingsQuery = queryOptions({
  queryKey: ['settings', 'contracts', 'general'],
  queryFn: () => apiGet<GeneralSettings>('/settings/contracts/general'),
})

export function saveContractType(kind: string, fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean; id: number }>(`/settings/contracts/general/${kind}`, fields)
}

export function setContractTypeStatus(kind: string, id: number, status: number) {
  return apiPost<{ saved: boolean; status: number }>(`/settings/contracts/general/${kind}/${id}/status`, { status })
}

export function removeMeasurementUnit(id: number) {
  return apiPost<{ removed: boolean }>(`/settings/contracts/general/measurement_unit_type/${id}/remove`, {})
}

/* -------------------------------------------------------------------------
 * Services (serviceList)
 * ---------------------------------------------------------------------- */

export type ServiceOption = { ID: number; name: string; has_quantity?: number; has_category?: number }

export type ServiceForm = {
  serviceCategories: ServiceOption[]
  priorities: ServiceOption[]
  coverages: ServiceOption[]
  responseSla: ServiceOption[]
  interventionSla: ServiceOption[]
  resolveSla: ServiceOption[]
  measurementUnits: ServiceOption[]
  properties: { id: number; name: string }[]
  currencies: string[]
  interventionLabel: string
  canCreate: boolean
  canEdit: boolean
}

export const serviceFormQuery = queryOptions({
  queryKey: ['settings', 'contracts', 'services', 'form'],
  queryFn: () => apiGet<ServiceForm>('/settings/contracts/services/form'),
})

export type ServiceRow = {
  ID: number
  service_name: string
  service_category_id: number
  service_category_name: string
  priority_id: number
  priority_name: string
  coverage_id: number
  coverage_name: string
  sla_id: number
  sla_name: string
  intervention_sla_id: number
  intervention_sla_name: string
  resolve_sla_id: number
  resolve_sla_name: string
  measurement_unit_id: number
  measurement_unit_name: string
  has_quantity: number
  estimate_unit_cost: string
  internal_cost: string
  currency: string
  quantity: string
  property_id: number
  description: string
  status: number
  DT_RowId?: string
}

export function servicesQuery(r: ContractsTableRequest) {
  return queryOptions({
    queryKey: ['settings', 'contracts', 'services', 'list', r],
    queryFn: () => apiGet<TablePage<ServiceRow>>(`/settings/contracts/services?${contractsTableParams(r).toString()}`),
  })
}

export function saveService(fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean; id: number }>('/settings/contracts/services', fields)
}

/** Raspunde cu `enabled` / `disabled`, ca `toggleStatus` din portal. */
export function toggleService(id: number) {
  return apiPost<{ status: string }>(`/settings/contracts/services/${id}/toggle`, {})
}

/* Felurile de tichet ale unui serviciu (randul desfasurat din lista). */

export type ServiceTicketType = { id: number; typeName: string; shortName: string; color: string }

export type ServiceTicketTypes = { serviceId: number; rows: ServiceTicketType[]; canEdit: boolean }

export function serviceTicketTypesQuery(serviceId: number) {
  return queryOptions({
    queryKey: ['settings', 'contracts', 'services', serviceId, 'ticket_types'],
    queryFn: () => apiGet<ServiceTicketTypes>(`/settings/contracts/services/${serviceId}/ticket_types`),
  })
}

export function ticketTypeChoicesQuery(q: string) {
  return queryOptions({
    queryKey: ['settings', 'contracts', 'ticket_types', q],
    queryFn: () =>
      apiGet<{ rows: { id: number; text: string }[] }>(
        `/settings/contracts/ticket_types?q=${encodeURIComponent(q)}`,
      ),
  })
}

export function addServiceTicketType(serviceId: number, ticketTypeId: number) {
  return apiPost<{ status: string }>(`/settings/contracts/services/${serviceId}/ticket_types`, {
    ticket_type_id: ticketTypeId,
  })
}

export function removeServiceTicketType(serviceId: number, ticketTypeId: number) {
  return apiPost<{ status: string }>(
    `/settings/contracts/services/${serviceId}/ticket_types/${ticketTypeId}/remove`,
    {},
  )
}

/* -------------------------------------------------------------------------
 * Service Properties
 * ---------------------------------------------------------------------- */

export type PropertyForm = {
  coverages: ServiceOption[]
  responseSla: ServiceOption[]
  interventionSla: ServiceOption[]
  resolveSla: ServiceOption[]
  canCreate: boolean
  canEdit: boolean
}

export const propertyFormQuery = queryOptions({
  queryKey: ['settings', 'contracts', 'service_properties', 'form'],
  queryFn: () => apiGet<PropertyForm>('/settings/contracts/service_properties/form'),
})

export type PropertyRow = {
  id: number
  name: string
  coverage: string
  coverage_id: number
  response_sla: string
  response_sla_id: number
  intervention_sla: string
  intervention_sla_id: number
  resolve_sla: string
  resolve_sla_id: number
  user_fullname: string
  created_at: string
  DT_RowId?: string
}

export function servicePropertiesQuery(r: ContractsTableRequest) {
  return queryOptions({
    queryKey: ['settings', 'contracts', 'service_properties', 'list', r],
    queryFn: () =>
      apiGet<TablePage<PropertyRow>>(`/settings/contracts/service_properties?${contractsTableParams(r).toString()}`),
  })
}

/** Fara `id` creeaza (`storeProperty`), cu `id` schimba (`updateProperty`). */
export function saveServiceProperty(fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean; id: number }>('/settings/contracts/service_properties', fields)
}

export function removeServiceProperty(id: number) {
  return apiPost<{ removed: boolean }>(`/settings/contracts/service_properties/${id}/remove`, {})
}

/* -------------------------------------------------------------------------
 * Customers Contracts
 * ---------------------------------------------------------------------- */

export type ContractRow = {
  ID: number
  company_id: number
  company_name: string
  contract_number: string
  description: string
  contract_type_id: number
  contract_type: string
  contract_template_id: number
  contract_template: string
  start_date: string
  end_date: string
  contract_cost: string
  contract_currency: string
  created: string
  status: number
  has_document_access: number
  has_edit_access: number
  DT_RowId?: string
}

export function contractsQuery(r: ContractsTableRequest) {
  return queryOptions({
    queryKey: ['settings', 'contracts', 'contracts', r],
    queryFn: () =>
      apiGet<TablePage<ContractRow>>(`/settings/contracts/contracts?${contractsTableParams(r).toString()}`),
  })
}

export type ContractServiceRow = {
  ID: number
  service_id: number
  service_name: string
  service_category: string
  priority: string
  coverage: string
  response_sla: string
  intervention_sla: string
  resolve_sla: string
  measurement_unit: string
  service_quantity: string
  unit_cost: string
  unit_remaining: string
  currency: string
  description: string
  has_edit_access: number
  DT_RowId?: string
}

export function contractServicesQuery(contractId: number, r: ContractsTableRequest) {
  return queryOptions({
    queryKey: ['settings', 'contracts', 'contracts', contractId, 'services', r],
    queryFn: () =>
      apiGet<TablePage<ContractServiceRow>>(
        `/settings/contracts/contracts/${contractId}/services?${contractsTableParams(r).toString()}`,
      ),
  })
}

export const contractNumberQuery = queryOptions({
  queryKey: ['settings', 'contracts', 'contracts', 'number'],
  queryFn: () => apiGet<{ number: number }>('/settings/contracts/contracts/number'),
})

export function saveContract(fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean; id: number }>('/settings/contracts/contracts', fields)
}

/* Documente */

export type DocumentRow = {
  id: number
  name: string
  path: string
  contract_id: number
  created_at: string
  DT_RowId?: string
}

export function contractDocumentsQuery(contractId: number, r: ContractsTableRequest) {
  return queryOptions({
    queryKey: ['settings', 'contracts', 'contracts', contractId, 'documents', r],
    queryFn: () =>
      apiGet<TablePage<DocumentRow>>(
        `/settings/contracts/contracts/${contractId}/documents?${contractsTableParams(r).toString()}`,
      ),
  })
}

/** Multipart, ca `ajaxForm` cu fisier din macheta veche. */
export function storeContractDocument(contractId: number, name: string, file: File) {
  const body = new FormData()
  body.append('document_name', name)
  body.append('document_file', file)
  return apiPostForm<{ saved: boolean; id: number }>(`/settings/contracts/contracts/${contractId}/documents`, body)
}

export function removeContractDocument(contractId: number, documentId: number) {
  return apiPost<{ removed: boolean }>(
    `/settings/contracts/contracts/${contractId}/documents/${documentId}/remove`,
    {},
  )
}

/** Adresa fisierului: se deschide direct, cookie-ul pleaca cu ea. */
export function contractDocumentUrl(contractId: number, documentId: number): string {
  return `/api/v2/settings/contracts/contracts/${contractId}/documents/${documentId}/file`
}
